import { Mail, MapPin, Phone, Star, User as UserIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LocationPicker } from "@/components/LocationPicker";
import { formatDate } from "@/lib/utils";
import type { ClienteDetail } from "./schemas";
import { PortalCredencialesCard } from "./PortalCredencialesCard";
import { TokuClabeCard } from "./TokuClabeCard";

/**
 * Tab "General" — contenido reutilizable del detalle de cliente.
 * Lee un ClienteDetail y renderiza datos fiscales, dirección y contactos.
 * Usado en `ClienteDetailPage` (página completa) — antes estaba dentro del drawer.
 */
export function ClienteGeneralTab({ cliente }: { cliente: ClienteDetail }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Datos generales */}
      <Card>
        <CardHeader>
          <CardTitle>Datos generales</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[120px_1fr] gap-y-1.5 text-xs">
            <dt className="text-muted-foreground">Código</dt>
            <dd className="font-mono">{cliente.codigo}</dd>
            <dt className="text-muted-foreground">Tipo</dt>
            <dd>
              <Badge>{cliente.tipo}</Badge>
            </dd>
            <dt className="text-muted-foreground">RFC</dt>
            <dd className="font-mono">{cliente.rfc}</dd>
            {cliente.curp && (
              <>
                <dt className="text-muted-foreground">CURP</dt>
                <dd className="font-mono">{cliente.curp}</dd>
              </>
            )}
            {cliente.nombre_comercial && (
              <>
                <dt className="text-muted-foreground">Comercial</dt>
                <dd>{cliente.nombre_comercial}</dd>
              </>
            )}
            <dt className="text-muted-foreground">Alta</dt>
            <dd>{formatDate(cliente.fecha_alta)}</dd>
            {cliente.fecha_baja && (
              <>
                <dt className="text-muted-foreground">Baja</dt>
                <dd>{formatDate(cliente.fecha_baja)}</dd>
              </>
            )}
            {cliente.motivo_baja && (
              <>
                <dt className="text-muted-foreground">Motivo</dt>
                <dd>{cliente.motivo_baja}</dd>
              </>
            )}
          </dl>
          {cliente.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {cliente.tags.map((t) => (
                <Badge key={t} variant="outline">
                  {t}
                </Badge>
              ))}
            </div>
          )}
          {cliente.notas && (
            <p className="mt-3 rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
              {cliente.notas}
            </p>
          )}
          {cliente.notas_internas && (
            <p className="mt-2 rounded-md border border-amber-300/50 bg-amber-50/60 dark:bg-amber-950/20 p-2 text-xs text-amber-800 dark:text-amber-300">
              <strong>Interna:</strong> {cliente.notas_internas}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Contactos */}
      <Card>
        <CardHeader>
          <CardTitle>
            Contactos <span className="text-muted-foreground">({cliente.contactos.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cliente.contactos.length === 0 ? (
            <p className="text-xs text-muted-foreground">Sin contactos registrados.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {cliente.contactos.map((c) => (
                <li
                  key={c.id}
                  className="flex items-start gap-2 rounded-md border p-2 text-xs"
                >
                  <UserIcon size={14} className="mt-0.5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium">{c.nombre}</span>
                      {c.es_principal && (
                        <Star size={10} className="text-amber-500" />
                      )}
                      {c.puesto && (
                        <span className="text-muted-foreground">— {c.puesto}</span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-3 text-muted-foreground">
                      {c.telefono && (
                        <span className="inline-flex items-center gap-1">
                          <Phone size={10} />
                          {c.telefono}
                        </span>
                      )}
                      {c.email && (
                        <span className="inline-flex items-center gap-1">
                          <Mail size={10} />
                          {c.email}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Portal cliente — credenciales mi.bytecom.mx */}
      <PortalCredencialesCard
        clienteId={cliente.id}
        numeroCliente={cliente.codigo}
      />

      {/* rc47: CLABE Toku + portal Toku */}
      <TokuClabeCard
        clienteId={cliente.id}
        clienteNombre={cliente.razon_social}
        clienteCodigo={cliente.codigo}
        empresaId={cliente.empresa_id}
      />

      {/* Datos fiscales */}
      {cliente.datos_fiscales && Object.keys(cliente.datos_fiscales).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Datos fiscales</CardTitle>
          </CardHeader>
          <CardContent>
            <FiscalesView data={cliente.datos_fiscales} />
          </CardContent>
        </Card>
      )}

      {/* Dirección instalación */}
      {cliente.direccion_instalacion &&
        Object.keys(cliente.direccion_instalacion).length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>
                <MapPin size={12} className="inline mr-1" />
                Dirección de instalación
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <DireccionView data={cliente.direccion_instalacion} />
              {(() => {
                const lat = cliente.direccion_instalacion?.latitud;
                const lng = cliente.direccion_instalacion?.longitud;
                const latNum = lat === null || lat === undefined || lat === "" ? null : Number(lat);
                const lngNum = lng === null || lng === undefined || lng === "" ? null : Number(lng);
                if (latNum === null || lngNum === null || isNaN(latNum) || isNaN(lngNum)) {
                  return (
                    <div className="flex items-center justify-center rounded-md border border-dashed bg-muted/20 p-6 text-center text-xs text-muted-foreground">
                      Sin coordenadas registradas
                    </div>
                  );
                }
                return (
                  <LocationPicker
                    value={{ lat: latNum, lng: lngNum }}
                    onChange={() => undefined}
                    readOnly
                    heightPx={200}
                  />
                );
              })()}
            </CardContent>
          </Card>
        )}
    </div>
  );
}

function FiscalesView({ data }: { data: Record<string, unknown> }) {
  const addr = [data.calle, data.numero_exterior && `#${data.numero_exterior}`, data.colonia]
    .filter(Boolean)
    .join(" ");
  const loc = [data.municipio, data.estado, data.codigo_postal].filter(Boolean).join(", ");
  return (
    <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs">
      {data.regimen_fiscal ? (
        <>
          <dt className="text-muted-foreground">Régimen</dt>
          <dd className="font-mono">{String(data.regimen_fiscal)}</dd>
        </>
      ) : null}
      {data.uso_cfdi_default ? (
        <>
          <dt className="text-muted-foreground">Uso CFDI</dt>
          <dd className="font-mono">{String(data.uso_cfdi_default)}</dd>
        </>
      ) : null}
      {data.email_fiscal ? (
        <>
          <dt className="text-muted-foreground">Email</dt>
          <dd>{String(data.email_fiscal)}</dd>
        </>
      ) : null}
      {addr ? (
        <>
          <dt className="text-muted-foreground">Dirección</dt>
          <dd>{addr}</dd>
        </>
      ) : null}
      {loc ? (
        <>
          <dt className="text-muted-foreground">Localidad</dt>
          <dd>{loc}</dd>
        </>
      ) : null}
    </dl>
  );
}

function DireccionView({ data }: { data: Record<string, unknown> }) {
  const addr = [data.calle, data.numero_exterior && `#${data.numero_exterior}`, data.colonia]
    .filter(Boolean)
    .join(" ");
  const loc = [data.municipio, data.estado, data.codigo_postal].filter(Boolean).join(", ");
  return (
    <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs">
      {addr && (
        <>
          <dt className="text-muted-foreground">Dirección</dt>
          <dd>{addr}</dd>
        </>
      )}
      {loc && (
        <>
          <dt className="text-muted-foreground">Localidad</dt>
          <dd>{loc}</dd>
        </>
      )}
      {data.referencias ? (
        <>
          <dt className="text-muted-foreground">Referencias</dt>
          <dd>{String(data.referencias)}</dd>
        </>
      ) : null}
      {(data.latitud || data.longitud) && (
        <>
          <dt className="text-muted-foreground">Coordenadas</dt>
          <dd className="font-mono">
            {String(data.latitud ?? "?")}, {String(data.longitud ?? "?")}
          </dd>
        </>
      )}
    </dl>
  );
}
